/** Delivery (B05-001..004, B06-001..003): week swimlanes, deliverables + versions, alerts, monthly reports, approvals. */
import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { Button, Heading, Panel, Text, ToneChip } from '@qm/ui';
import type { DeliverableState } from '@qm/contracts/deliverables';
import { useSurface } from '../kit/context';
import { useAction, useResource } from '../kit/resource';
import { Badge, DataTable, ErrorNote, Field, Loadable, Notice, Screen, Stat, Tabs, When, type Tone } from '../kit/ui';
import type { DeliverableView, WeekItem } from '../api';
import { CAL_STATUSES, approvalChain, calStatus, channelOptions, dayOfMonth, filterWeekItems, longDay, nextDue, ownerOptions, requestApprovalTarget, shortWeekday, timeOf, timelineGroups, type CalStatus } from '../lib/views';
import { MAX_PUBLISH_ATTEMPTS, approvalStage, isOverdue, isoDay, manualTargets, partialSections, publishState, startOfWeek, swimlanes, versionHistory, weekDays } from '../lib/logic';
import { usePartnerApi } from './layout';
import { urlFieldProps, withHttps } from '../../../lib/url';

const STATE_TONE: Partial<Record<string, Tone>> = { approved: 'good', published: 'accent', changes_requested: 'warn', rejected: 'bad', cancelled: 'neutral' };

type CalView = 'calendar' | 'timeline';

export function CalendarPage() {
  const { t, now, locale } = useSurface();
  const api = usePartnerApi();
  const [sp, setSp] = useSearchParams();
  const start = sp.get('week') ?? isoDay(startOfWeek(now()));
  const view: CalView = sp.get('view') === 'timeline' ? 'timeline' : 'calendar';
  const clientFilter = sp.get('client') ?? '';
  const statusFilter = (CAL_STATUSES as readonly string[]).includes(sp.get('status') ?? '') ? sp.get('status')! : '';
  const channelFilter = sp.get('channel') ?? '';
  const ownerFilter = sp.get('owner') ?? '';
  const [selected, setSelected] = useState<string | null>(null);
  const week = useResource(`week:${start}`, () => api.week(start));
  const setParam = (k: string, v: string) => { const n = new URLSearchParams(sp); if (v) n.set(k, v); else n.delete(k); setSp(n); };
  const shift = (days: number) => { const d = new Date(`${start}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + days); const n = new URLSearchParams(sp); n.set('week', isoDay(d)); setSp(n); };
  return (
    <Screen title={t('portal.partners.calendar.heading')} actions={<>
      <Button onClick={() => shift(-7)}>{t('portal.partners.calendar.prev')}</Button>
      <Button onClick={() => shift(7)}>{t('portal.partners.calendar.next')}</Button>
      <Link className="qm-button" data-variant="accent" to="/deliverables/new">{t('portal.partners.calendar.create')}</Link>
    </>} testId="calendar">
      <Loadable resource={week}>
        {(w) => {
          const days = w.days?.length ? w.days : weekDays(new Date(`${start}T00:00:00Z`));
          const all = w.items;
          const items = filterWeekItems(all, { client: clientFilter, status: statusFilter, channel: channelFilter, owner: ownerFilter });
          const lanes = swimlanes(items, days);
          const customers = swimlanes(all, days).map((l) => ({ id: l.clientId, name: l.clientName }));
          const today = isoDay(now());
          const sel = items.find((i) => i.id === selected) ?? nextDue(items, now());
          if (all.length === 0) return <Notice>{t('portal.partners.calendar.empty')}</Notice>;
          return (
            <div className="qp-cal-wrap">
              <div className="qs-filters">
                <Field label={t('portal.partners.calendar.filter.client')}>
                  {(fid) => (
                    <select id={fid} name="client" value={clientFilter} onChange={(e) => setParam('client', e.target.value)}>
                      <option value="">{t('portal.partners.calendar.filter.all')}</option>
                      {customers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </Field>
                <Field label={t('portal.partners.calendar.filter.status')}>
                  {(fid) => (
                    <select id={fid} name="status" value={statusFilter} onChange={(e) => setParam('status', e.target.value)}>
                      <option value="">{t('portal.partners.calendar.filter.all')}</option>
                      {CAL_STATUSES.map((st) => <option key={st} value={st}>{t(`portal.partners.calstatus.${st}`)}</option>)}
                    </select>
                  )}
                </Field>
                <Field label={t('portal.partners.calendar.filter.channel')}>
                  {(fid) => (
                    <select id={fid} name="channel" value={channelFilter} onChange={(e) => setParam('channel', e.target.value)}>
                      <option value="">{t('portal.partners.calendar.filter.all')}</option>
                      {channelOptions(all).map((c) => <option key={c} value={c}>{t(`portal.partners.calendar.type.${c}`)}</option>)}
                    </select>
                  )}
                </Field>
                <Field label={t('portal.partners.calendar.filter.owner')} hint={ownerOptions(all).length ? undefined : t('portal.partners.calendar.filter.needsOwner')}>
                  {(fid, d) => (
                    <select id={fid} name="owner" value={ownerFilter} disabled={ownerOptions(all).length === 0} aria-describedby={d} onChange={(e) => setParam('owner', e.target.value)}>
                      <option value="">{t('portal.partners.calendar.filter.all')}</option>
                      {ownerOptions(all).map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                    </select>
                  )}
                </Field>
                <Button type="button" data-testid="calendar-clear" onClick={() => { const n = new URLSearchParams(sp); for (const k of ['client', 'status', 'channel', 'owner']) n.delete(k); setSp(n); }}>{t('portal.partners.calendar.filter.clear')}</Button>
                <div className="qs-seg" role="group" aria-label={t('portal.partners.calendar.view')}>
                  {(['calendar', 'timeline'] as const).map((v) => (
                    <button key={v} type="button" aria-pressed={view === v} data-view={v} onClick={() => setParam('view', v === 'calendar' ? '' : v)}>{t(`portal.partners.calendar.view.${v}`)}</button>
                  ))}
                </div>
              </div>
              <div className="qp-cal-split">
                <div className="qp-cal-wrap">
                  {items.length === 0 ? <Notice>{t('portal.partners.calendar.noMatch')}</Notice> : view === 'calendar' ? (
                    <div className="qs-cal" role="grid" aria-label={t('portal.partners.calendar.heading')}>
                      <div style={{ display: 'contents' }} role="row">
                        <div className="qs-cal__lane" role="columnheader">{t('portal.partners.calendar.client')}</div>
                        {days.map((d) => (
                          <div key={d} className="qs-cal__dayhead" role="columnheader" data-today={d === today ? 'true' : undefined} aria-current={d === today ? 'date' : undefined}>
                            <span>{shortWeekday(locale, d)}</span><b>{dayOfMonth(locale, d)}</b>
                          </div>
                        ))}
                      </div>
                      {lanes.map((l) => {
                        const n = Object.values(l.cells).reduce((a, c) => a + c.length, 0);
                        return (
                          <div key={l.clientId} style={{ display: 'contents' }} role="row" data-lane={l.clientId}>
                            <div className="qs-cal__lane" role="rowheader"><span>{l.clientName}</span><small>{t(n === 1 ? 'portal.partners.calendar.count.one' : 'portal.partners.calendar.count.other', { n })}</small></div>
                            {days.map((d) => (
                              <div key={d} className="qs-cal__cell" role="gridcell">
                                {(l.cells[d] ?? []).map((it) => <WeekCard key={it.id} item={it} selected={sel?.id === it.id} onSelect={() => setSelected(it.id)} />)}
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <ol className="qp-timeline" data-testid="calendar-timeline">
                      {timelineGroups(items, days).map((g) => (
                        <li key={g.day}>
                          <h3>{longDay(locale, g.day)}</h3>
                          <ul>{g.items.map((it) => <li key={it.id}><WeekCard item={it} selected={sel?.id === it.id} onSelect={() => setSelected(it.id)} /></li>)}</ul>
                        </li>
                      ))}
                    </ol>
                  )}
                  <div className="qs-legend" aria-label={t('portal.partners.calendar.legend')}>
                    {CAL_STATUSES.map((st) => <span key={st}><i className="qp-legend-swatch" data-status={st} aria-hidden="true" />{t(`portal.partners.calstatus.${st}`)}</span>)}
                  </div>
                  <Text tone="muted">{t('portal.partners.calendar.hint')}</Text>
                </div>
                <DueNext item={sel} />
              </div>
            </div>
          );
        }}
      </Loadable>
    </Screen>
  );
}

function WeekCard({ item, selected, onSelect }: { item: WeekItem; selected: boolean; onSelect: () => void }) {
  const { t, now } = useSurface();
  const overdue = isOverdue(item, now());
  const st = calStatus(item);
  return (
    <button type="button" className="qs-ev" data-status={st} aria-pressed={selected} onClick={onSelect} data-overdue={overdue} data-testid="week-card">
      {item.dueAt ? <time dateTime={item.dueAt}>{timeOf(item.dueAt)}</time> : null}
      <span className="qp-ev-title">{item.title}</span>
      <span className="qp-ev-marks">
        <ToneChip tone={CAL_CHIP[st]}>{t(`portal.partners.calstatus.${st}`)}</ToneChip>
        {item.internalReview ? <Badge testId="marker-internal">{t('portal.partners.calendar.internalReview')}</Badge> : null}
        {item.clientApproval ? <Badge tone={item.clientApproval === 'approved' ? 'good' : 'warn'} testId="marker-client">{t(`portal.partners.calendar.client.${item.clientApproval}`)}</Badge> : null}
        {overdue ? <Badge tone="bad" testId="marker-overdue">{t('portal.partners.calendar.overdue')}</Badge> : null}
      </span>
    </button>
  );
}
const CAL_CHIP: Record<CalStatus, 'lavender' | 'sun' | 'mint' | 'sky'> = { draft: 'lavender', internal: 'sky', customer: 'sun', scheduled: 'lavender', published: 'mint' };

/** Right panel: the selected (default: next due) item with its real deliverable record and approval path. */
function DueNext({ item }: { item: WeekItem | null }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [reload, setReload] = useState(0);
  const isDeliverable = !!item && item.type === 'deliverable';
  const d = useResource<DeliverableView | null>(`deliverable:${item?.id ?? 'none'}${reload ? `:${reload}` : ''}`, () => (isDeliverable ? api.deliverable(item!.id) : Promise.resolve(null)));
  const move = useAction((id: string, to: DeliverableState) => api.transition(id, to));
  if (!item) return <aside className="qs-aside" data-testid="due-next"><Heading level={2}>{t('portal.partners.calendar.dueNext')}</Heading><Text tone="muted">{t('portal.partners.calendar.dueNone')}</Text></aside>;
  const st = calStatus(item);
  const dv = d.status === 'ready' ? d.data : null;
  const state = dv?.state ?? item.state;
  const target = requestApprovalTarget(state);
  return (
    <aside className="qs-aside qp-due" aria-labelledby="due-next" data-testid="due-next">
      <Heading level={2} id="due-next">{t('portal.partners.calendar.dueNext')}</Heading>
      <div>
        {item.dueAt ? <span className="qp-due__time">{timeOf(item.dueAt)}</span> : null}
        <Heading level={3}>{item.title}</Heading>
        <Text tone="muted">{item.clientName}</Text>
      </div>
      <ToneChip tone={CAL_CHIP[st]}>{t(`portal.partners.calstatus.${st}`)}</ToneChip>
      <ul className="qs-kv">
        <li><span className="qs-kv__label">{t('portal.partners.calendar.client')}</span><span className="qs-kv__value">{item.clientName}</span></li>
        <li><span className="qs-kv__label">{t('portal.partners.deliverables.due')}</span><span className="qs-kv__value"><When iso={item.dueAt} /></span></li>
        {dv ? <li><span className="qs-kv__label">{t('portal.partners.deliverables.owner')}</span><span className="qs-kv__value" data-testid="due-owner"><bdi>{dv.ownerUserId}</bdi></span></li> : null}
      </ul>
      <section aria-label={t('portal.partners.calendar.chain')}>
        <span className="qs-micro">{t('portal.partners.calendar.chain')}</span>
        <ol className="qs-chain" data-testid="approval-chain">
          {approvalChain(state).map((c, i) => (
            <li key={c.step} data-state={c.state}>
              <span className="qs-chain__n" aria-hidden="true">{i + 1}</span>
              <span>{t(`portal.partners.calendar.chain.${c.step}`)}{c.state === 'current' ? <small>{t('portal.partners.calendar.chain.current')}</small> : null}</span>
            </li>
          ))}
        </ol>
      </section>
      {dv?.versions ? <Text tone="muted" data-testid="due-audit">{t('portal.partners.calendar.audit')}: {t('portal.partners.calendar.updates', { n: dv.versions.length })}</Text> : null}
      <div className="qp-actions">
        {isDeliverable ? <Link className="qm-button" to={`/deliverables/${encodeURIComponent(item.id)}`}>{t('portal.partners.calendar.open')}</Link> : null}
        {target ? <Button variant="primary" disabled={move.pending} onClick={async () => { if (await move.run(item.id, target)) setReload((n) => n + 1); }}>{t('portal.partners.calendar.requestApproval')}</Button> : null}
      </div>
      {target ? <Text tone="muted">{t('portal.partners.calendar.requestNote')}</Text> : null}
      <ErrorNote error={move.error} />
    </aside>
  );
}

export function DeliverablePage() {
  const api = usePartnerApi();
  const { id = '' } = useParams();
  const d = useResource(`deliverable:${id}`, () => api.deliverable(id));
  return <Loadable resource={d}>{(v) => <DeliverableDetail d={v} onChanged={d.reload} />}</Loadable>;
}

export function DeliverableDetail({ d, onChanged }: { d: DeliverableView; onChanged: () => void }) {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [url, setUrl] = useState('');
  const [note, setNote] = useState('');
  const version = useAction(() => api.addVersion(d.id, { note: note || undefined, attachments: [{ kind: 'link', url: withHttps(url) }] }));
  const move = useAction((to: DeliverableState) => api.transition(d.id, to));
  const { current, history } = versionHistory(d);
  const pub = publishState(d);
  const targets = manualTargets(d.state);
  return (
    <Screen title={d.title} subtitle={d.clientLabel ?? undefined} testId="deliverable">
      <div className="qx-grid">
        <Stat label={t('portal.partners.deliverables.state')} value={<Badge tone={STATE_TONE[d.state] ?? 'neutral'} testId="deliverable-state">{t(`portal.partners.dstate.${d.state}`)}</Badge>} />
        <Stat label={t('portal.partners.deliverables.version')} value={<span data-testid="current-version">v{d.currentVersion}</span>} />
        <Stat label={t('portal.partners.deliverables.due')} value={<When iso={d.dueAt} />} />
        <Stat label={t('portal.partners.deliverables.publish')} value={<Badge tone={pub === 'failed' ? 'bad' : pub === 'published' ? 'accent' : 'neutral'} testId={`publish-${pub}`}>{t(`portal.partners.publish.${pub}`, { n: d.publishAttempts ?? 0, max: MAX_PUBLISH_ATTEMPTS })}</Badge>} />
      </div>
      {pub === 'published' && d.publishedUrl ? <Text>{t('portal.partners.publish.url')}: <a href={d.publishedUrl} rel="noopener noreferrer" target="_blank">{d.publishedUrl}</a></Text> : null}
      {pub === 'not_approved' ? <Text tone="muted" data-testid="publish-blocked">{t('portal.partners.publish.onlyApproved')}</Text> : null}
      {targets.length ? (
        <Panel>
          <Heading level={2}>{t('portal.partners.deliverables.move')}</Heading>
          <div className="qx-toolbar" data-testid="transitions">
            {targets.map((s) => <Button key={s} data-to={s} disabled={move.pending} onClick={async () => { if (await move.run(s)) onChanged(); }}>{t(`portal.partners.dstate.${s}`)}</Button>)}
          </div>
          <ErrorNote error={move.error} />
        </Panel>
      ) : <Text tone="muted" data-testid="no-transitions">{t('portal.partners.deliverables.final')}</Text>}
      {d.state !== 'published' && d.state !== 'cancelled' ? (
        <Panel>
          <Heading level={2}>{t('portal.partners.deliverables.newVersion')}</Heading>
          <form onSubmit={async (e: FormEvent) => { e.preventDefault(); if (await version.run()) { setUrl(''); setNote(''); onChanged(); } }}>
            <Field label={t('portal.partners.deliverables.link')}>{(fid) => <input id={fid} type="url" name="url" required pattern="https://.*" value={url} onChange={(e) => setUrl(e.target.value)} {...urlFieldProps(url, setUrl)} />}</Field>
            <Field label={t('portal.partners.deliverables.note')}>{(fid) => <textarea id={fid} name="note" value={note} onChange={(e) => setNote(e.target.value)} />}</Field>
            <Button type="submit" variant="primary" disabled={!withHttps(url) || version.pending}>{t('portal.partners.deliverables.upload')}</Button>
          </form>
          <ErrorNote error={version.error} />
        </Panel>
      ) : null}
      <Panel>
        <Heading level={2}>{t('portal.partners.deliverables.history')}</Heading>
        {current ? <Text data-testid="version-current">v{current.version} ({t('portal.partners.deliverables.current')}) {current.note ?? ''}</Text> : null}
        <ul data-testid="version-history">{history.map((v) => <li key={v.version}>v{v.version} - <When iso={v.createdAt} /> {v.note ?? ''}</li>)}</ul>
      </Panel>
    </Screen>
  );
}

export function NewDeliverablePage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const nav = useNavigate();
  const [f, setF] = useState({ title: '', ownerUserId: '', dueAt: '', clientLabel: '', link: '' });
  const act = useAction(api.createDeliverable);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const r = await act.run({
      title: f.title.trim(), ownerUserId: f.ownerUserId.trim(), ...(f.dueAt ? { dueAt: new Date(f.dueAt).toISOString() } : {}),
      ...(f.clientLabel ? { clientLabel: f.clientLabel } : {}), attachments: withHttps(f.link) ? [{ kind: 'link', url: withHttps(f.link) }] : [],
    });
    if (r) nav(`/deliverables/${encodeURIComponent(r.id)}`);
  };
  const input = (k: keyof typeof f, label: string, type = 'text') => (
    <Field label={label}>{(id) => <input id={id} name={k} type={type} value={f[k]} onChange={(e) => setF((x) => ({ ...x, [k]: e.target.value }))} {...(type === 'url' ? urlFieldProps(f[k], (v) => setF((x) => ({ ...x, [k]: v }))) : {})} />}</Field>
  );
  return (
    <Screen title={t('portal.partners.deliverables.new')} testId="deliverable-new">
      <form onSubmit={submit}>
        {input('title', t('portal.partners.deliverables.titleField'))}
        {input('ownerUserId', t('portal.partners.deliverables.owner'))}
        {input('dueAt', t('portal.partners.deliverables.due'), 'datetime-local')}
        {input('clientLabel', t('portal.partners.calendar.client'))}
        {input('link', t('portal.partners.deliverables.link'), 'url')}
        <Button type="submit" variant="primary" disabled={!f.title.trim() || !f.ownerUserId.trim() || act.pending}>{t('portal.partners.deliverables.create')}</Button>
      </form>
      <ErrorNote error={act.error} />
    </Screen>
  );
}

export function ReportsPage() {
  const { t } = useSurface();
  const api = usePartnerApi();
  const [sp, setSp] = useSearchParams();
  const status = sp.get('status') ?? '';
  const list = useResource(`reports:${status}`, () => api.monthlyReports(status ? { status } : {}));
  const submit = useAction(api.submitReport);
  const render = useAction(api.renderReport);
  const archive = useAction(api.archiveReport);
  const statuses = ['', 'draft', 'awaiting_approval', 'changes_requested', 'approved', 'published', 'archived'];
  return (
    <Screen title={t('portal.partners.reports.title')} testId="reports">
      <Tabs label={t('portal.partners.reports.title')} active={status} onSelect={(v) => setSp(v ? { status: v } : {})} tabs={statuses.map((s) => ({ id: s, label: t(`portal.partners.reports.status.${s || 'all'}`) }))} />
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(r) => r.id} empty={t('portal.kit.empty')} columns={[
          { key: 'client', header: t('portal.partners.clients.col.name'), render: (r) => r.clientName ?? r.clientTenantId ?? '' },
          { key: 'period', header: t('portal.partners.reports.period'), render: (r) => r.period },
          { key: 'status', header: t('portal.partners.clients.col.status'), render: (r) => <Badge tone={r.status === 'published' ? 'accent' : r.status === 'approved' ? 'good' : 'neutral'}>{t(`portal.partners.reports.status.${r.status}`)}</Badge> },
          {
            key: 'partial', header: t('portal.partners.reports.data'), render: (r) => {
              const p = partialSections(r);
              return p.length ? <span data-testid="partial-data">{p.map((s) => t('portal.partners.reports.partial', { source: s.source, reason: s.reason ?? '-' })).join('; ')}</span> : t('portal.partners.reports.complete');
            },
          },
          {
            key: 'act', header: '', render: (r) => (
              <span className="qx-toolbar">
                {r.status === 'draft' || r.status === 'changes_requested' ? <Button onClick={async () => { if (await submit.run(r.id)) list.reload(); }}>{t('portal.partners.reports.submit')}</Button> : null}
                {r.status === 'approved' || r.status === 'published' ? <Button onClick={() => void render.run(r.id)}>{t('portal.partners.reports.pdf')}</Button> : null}
                {r.status === 'published' ? <Button onClick={async () => { if (await archive.run(r.id)) list.reload(); }}>{t('portal.partners.reports.archive')}</Button> : null}
              </span>
            ),
          },
        ]} />}
      </Loadable>
      <ErrorNote error={submit.error ?? render.error ?? archive.error} />
    </Screen>
  );
}

/** Approval queue with reminder (24 h) and escalation (48 h) markers (B06-003) and client decision state (B06-001). */
export function ApprovalsPage() {
  const { t, now } = useSurface();
  const api = usePartnerApi();
  const list = useResource('approvals:pending', () => api.approvals({ status: 'pending' }));
  return (
    <Screen title={t('portal.partners.approvals.title')} subtitle={t('portal.partners.approvals.subtitle')} testId="approvals">
      <Loadable resource={list}>
        {(rows) => <DataTable rows={rows} rowKey={(a) => a.id} empty={t('portal.partners.approvals.empty')} columns={[
          { key: 'title', header: t('portal.partners.approvals.item'), render: (a) => a.title },
          { key: 'client', header: t('portal.partners.clients.col.name'), render: (a) => a.clientName ?? a.tenantId ?? '' },
          { key: 'since', header: t('portal.partners.approvals.since'), narrow: 'hide', render: (a) => <When iso={a.requestedAt} /> },
          {
            key: 'stage', header: t('portal.partners.approvals.stage'), render: (a) => {
              const s = approvalStage(a, now());
              return <Badge tone={s === 'escalated' ? 'bad' : s === 'reminded' ? 'warn' : 'neutral'} testId={`approval-${s}`}>{t(`portal.partners.approvals.stage.${s}`)}</Badge>;
            },
          },
        ]} />}
      </Loadable>
    </Screen>
  );
}
